import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { widgetMeta } from "@/lib/widgets";
import { useAnalytics, useTrades, useAccountsSummary, useJournal, useVersions } from "@/hooks/useData";
import {
  EquityCurveChart, MonthlyPerfChart, DrawdownChart, SymbolDonut, DistributionChart, TradingHoursChart,
} from "@/components/widgets/ChartWidgets";
import { DirBadge } from "@/components/widgets/ListWidgets";
import { Sparkline } from "@/components/widgets/StatWidgets";
import { fmtMoney, fmtSignedMoney, fmtPct, fmtNum, fmtDate, fmtDateTime, fmtHold, pnlClass, rangeLabel, fmtMonth } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

function StatCell({ label, value, className }) {
  return (
    <div className="rounded-xl border bg-surface-2 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 text-sm font-semibold tabular-nums", className)}>{value}</div>
    </div>
  );
}

function SummaryGrid({ data }) {
  const s = data?.summary;
  const cur = data?.account?.currency || "USD";
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <StatCell label="Balance" value={fmtMoney(s.balance, cur)} />
      <StatCell label="Equity" value={fmtMoney(s.equity, cur)} />
      <StatCell label="Total gain" value={fmtPct(s.total_gain_pct, true)} className={pnlClass(s.total_gain_pct)} />
      <StatCell label="Monthly P/L" value={fmtSignedMoney(s.monthly_profit, cur)} className={pnlClass(s.monthly_profit)} />
      <StatCell label="Win rate" value={`${s.win_rate}%`} />
      <StatCell label="Profit factor" value={s.profit_factor} className={s.profit_factor >= 1 ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"} />
      <StatCell label="Max drawdown" value={`-${fmtPct(s.max_drawdown)}`} className="text-[hsl(var(--loss))]" />
      <StatCell label="Expectancy" value={fmtSignedMoney(s.expectancy, cur)} className={pnlClass(s.expectancy)} />
      <StatCell label="Trades" value={fmtNum(s.total_trades)} />
      <StatCell label="Avg win / loss" value={`${fmtMoney(s.avg_win, cur)} / ${fmtMoney(s.avg_loss, cur)}`} />
      <StatCell label="Best / worst" value={`${fmtSignedMoney(s.best_trade, cur)} / ${fmtSignedMoney(s.worst_trade, cur)}`} />
      <StatCell label="Net pips" value={`${s.pips_total > 0 ? "+" : ""}${fmtNum(s.pips_total, 1)}`} className={pnlClass(s.pips_total)} />
      <StatCell label="Lots traded" value={fmtNum(s.lots, 1)} />
      <StatCell label="Commissions" value={fmtMoney(s.commissions, cur)} />
      <StatCell label="Avg hold" value={fmtHold(s.avg_hold_minutes)} />
      <StatCell label="Sharpe (approx)" value={s.sharpe} />
    </div>
  );
}

function TradesTable({ accountId }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useTrades(accountId, page);
  if (isLoading) return <Skeleton className="h-64 w-full" />;
  const trades = data?.trades || [];
  const pages = Math.max(1, Math.ceil((data?.total || 0) / (data?.limit || 25)));
  return (
    <div>
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-xs" data-testid="drilldown-trades-table">
          <thead className="bg-surface-2">
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 font-medium">Closed</th>
              <th className="px-3 py-2 font-medium">Symbol</th>
              <th className="px-3 py-2 font-medium">Side</th>
              <th className="px-3 py-2 text-right font-medium">Lots</th>
              <th className="px-3 py-2 text-right font-medium">Open</th>
              <th className="px-3 py-2 text-right font-medium">Close</th>
              <th className="px-3 py-2 text-right font-medium">Pips</th>
              <th className="px-3 py-2 text-right font-medium">Net P/L</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => {
              const net = t.profit + t.commission + t.swap;
              return (
                <tr key={t.trade_id} className="border-t hover:bg-muted/40">
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[10px] text-muted-foreground">{fmtDateTime(t.close_time)}</td>
                  <td className="px-3 py-1.5 font-medium">{t.symbol}</td>
                  <td className="px-3 py-1.5"><DirBadge type={t.type} /></td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{t.lots}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{t.open_price}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{t.close_price}</td>
                  <td className={cn("px-3 py-1.5 text-right tabular-nums", pnlClass(t.pips))}>{t.pips > 0 ? "+" : ""}{t.pips}</td>
                  <td className={cn("px-3 py-1.5 text-right font-medium tabular-nums", pnlClass(net))}>{fmtSignedMoney(net)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>{fmtNum(data?.total)} trades · page {page} of {pages}</span>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="trades-prev-page">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="trades-next-page">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function MonthlyTable({ data }) {
  const rows = [...(data?.monthly || [])].reverse().slice(0, 12);
  return (
    <div className="overflow-hidden rounded-xl border">
      <table className="w-full text-xs">
        <thead className="bg-surface-2">
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium">Month</th>
            <th className="px-3 py-2 text-right font-medium">Profit</th>
            <th className="px-3 py-2 text-right font-medium">Gain</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.month} className="border-t">
              <td className="px-3 py-1.5">{fmtMonth(m.month)}</td>
              <td className={cn("px-3 py-1.5 text-right tabular-nums", pnlClass(m.profit))}>{fmtSignedMoney(m.profit)}</td>
              <td className={cn("px-3 py-1.5 text-right tabular-nums", pnlClass(m.gain_pct))}>{fmtPct(m.gain_pct, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SymbolTable({ data }) {
  return (
    <div className="overflow-hidden rounded-xl border">
      <table className="w-full text-xs">
        <thead className="bg-surface-2">
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium">Symbol</th>
            <th className="px-3 py-2 text-right font-medium">Trades</th>
            <th className="px-3 py-2 text-right font-medium">Win rate</th>
            <th className="px-3 py-2 text-right font-medium">Pips</th>
            <th className="px-3 py-2 text-right font-medium">Profit</th>
          </tr>
        </thead>
        <tbody>
          {(data?.symbols || []).map((s) => (
            <tr key={s.symbol} className="border-t">
              <td className="px-3 py-1.5 font-medium">{s.symbol}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{s.trades}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{s.win_rate}%</td>
              <td className={cn("px-3 py-1.5 text-right tabular-nums", pnlClass(s.pips))}>{s.pips > 0 ? "+" : ""}{fmtNum(s.pips, 1)}</td>
              <td className={cn("px-3 py-1.5 text-right font-medium tabular-nums", pnlClass(s.profit))}>{fmtSignedMoney(s.profit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SharedListsDetail({ type, item, shareToken, accountId }) {
  const { data: summary } = useAccountsSummary(shareToken);
  const { data: journal } = useJournal(shareToken);
  const { data: versions } = useVersions(item.settings?.account_id, shareToken);

  if (type === "accounts_list" || type === "account_comparison") {
    const rows = (summary || []).filter((r) => r.status !== "pending");
    return (
      <div className="overflow-hidden rounded-xl border">
        <table className="w-full text-xs" data-testid="drilldown-accounts-table">
          <thead className="bg-surface-2">
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 font-medium">Account</th>
              <th className="px-3 py-2 text-right font-medium">Balance</th>
              <th className="px-3 py-2 text-right font-medium">Gain</th>
              <th className="px-3 py-2 text-right font-medium">Win rate</th>
              <th className="px-3 py-2 text-right font-medium">PF</th>
              <th className="px-3 py-2 text-right font-medium">Max DD</th>
              <th className="px-3 py-2 text-right font-medium">Risk</th>
              <th className="px-3 py-2 text-right font-medium">Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.account_id} className="border-t">
                <td className="px-3 py-2">
                  <div className="font-medium">{a.name}</div>
                  <div className="text-[10px] text-muted-foreground">{a.broker} · {a.platform}</div>
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">{fmtMoney(a.balance, a.currency)}</td>
                <td className={cn("px-3 py-2 text-right tabular-nums", pnlClass(a.total_gain_pct))}>{fmtPct(a.total_gain_pct, true)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.win_rate}%</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.profit_factor}</td>
                <td className="px-3 py-2 text-right tabular-nums text-[hsl(var(--loss))]">-{fmtPct(a.max_drawdown)}</td>
                <td className="px-3 py-2 text-right"><Badge variant="secondary" className="text-[10px]">{a.risk?.label}</Badge></td>
                <td className="px-3 py-2 text-right">
                  <Sparkline points={a.sparkline} width={80} height={22}
                    className={a.total_gain_pct >= 0 ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (type === "journal") {
    return (
      <div className="space-y-3">
        {(journal || []).map((e) => (
          <div key={e.entry_id} className="rounded-xl border bg-surface-2 p-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{e.title}</h4>
              <span className="font-mono text-[10px] text-muted-foreground">{fmtDate(e.date)}</span>
            </div>
            <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">{e.content}</p>
          </div>
        ))}
        {!journal?.length && <p className="py-6 text-center text-xs text-muted-foreground">No journal entries yet.</p>}
      </div>
    );
  }
  if (type === "version_history") {
    return (
      <div className="space-y-3">
        {(versions || []).map((v) => (
          <div key={v.version_id} className="rounded-xl border bg-surface-2 p-4">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-primary">{v.version}</span>
              <span className="text-sm font-medium">{v.title}</span>
              {v.impact && <Badge variant="secondary" className="text-[10px]">{v.impact}</Badge>}
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{v.notes}</p>
            <div className="mt-1.5 font-mono text-[10px] text-muted-foreground/70">{fmtDate(v.released_at)}</div>
          </div>
        ))}
        {!versions?.length && <p className="py-6 text-center text-xs text-muted-foreground">No versions logged.</p>}
      </div>
    );
  }
  return null;
}

/**
 * Consistent drill-down: every widget opens the same detail pattern -
 * a headline chart/table plus the full stat grid behind the number.
 */
export default function DrilldownModal({ item, defaults, accounts, shareToken, open, onClose }) {
  const type = item?.widget_type;
  const meta = item ? widgetMeta(type) : null;
  const accountId = item?.settings?.account_id || defaults?.account_id;
  const range = item?.settings?.range || defaults?.range || "all";
  const account = accounts?.find((a) => a.account_id === accountId);
  const needsAnalytics = meta && (meta.category === "stats" || meta.category === "charts" || ["recent_trades", "open_positions"].includes(type));
  const { data, isLoading } = useAnalytics(open && needsAnalytics ? accountId : null, range, shareToken);

  if (!item) return null;

  const renderDetail = () => {
    if (needsAnalytics && isLoading) return <Skeleton className="h-72 w-full" />;
    switch (type) {
      case "equity_curve":
        return (
          <div className="space-y-4">
            <EquityCurveChart data={data} height={300} currency={data?.account?.currency} />
            <SummaryGrid data={data} />
          </div>
        );
      case "monthly_performance":
        return (
          <div className="space-y-4">
            <MonthlyPerfChart data={data} height={280} />
            <MonthlyTable data={data} />
          </div>
        );
      case "drawdown_chart":
        return (
          <div className="space-y-4">
            <DrawdownChart data={data} height={280} />
            <div className="grid grid-cols-2 gap-2">
              <StatCell label="Max drawdown" value={`-${fmtPct(data?.summary?.max_drawdown)}`} className="text-[hsl(var(--loss))]" />
              <StatCell label="Current drawdown" value={fmtPct(Math.abs(data?.summary?.current_drawdown || 0))} />
            </div>
          </div>
        );
      case "symbol_breakdown":
        return (
          <div className="space-y-4">
            <SymbolDonut data={data} height={260} />
            <SymbolTable data={data} />
          </div>
        );
      case "win_loss_distribution":
        return (
          <div className="space-y-4">
            <DistributionChart data={data} height={280} />
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <StatCell label="Avg win" value={fmtMoney(data?.summary?.avg_win)} className="text-[hsl(var(--profit))]" />
              <StatCell label="Avg loss" value={fmtMoney(data?.summary?.avg_loss)} className="text-[hsl(var(--loss))]" />
              <StatCell label="Best trade" value={fmtSignedMoney(data?.summary?.best_trade)} className="text-[hsl(var(--profit))]" />
              <StatCell label="Worst trade" value={fmtSignedMoney(data?.summary?.worst_trade)} className="text-[hsl(var(--loss))]" />
            </div>
          </div>
        );
      case "trading_hours":
        return (
          <div className="space-y-4">
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">By hour of day (UTC)</p>
              <TradingHoursChart data={data} height={200} mode="hours" />
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">By weekday</p>
              <TradingHoursChart data={data} height={180} mode="weekdays" />
            </div>
          </div>
        );
      case "recent_trades":
        return shareToken ? (
          <div className="space-y-4">
            <SummaryGrid data={data} />
          </div>
        ) : (
          <TradesTable accountId={accountId} />
        );
      case "open_positions":
        return (
          <div className="overflow-hidden rounded-xl border">
            <table className="w-full text-xs">
              <thead className="bg-surface-2">
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Opened</th>
                  <th className="px-3 py-2 font-medium">Symbol</th>
                  <th className="px-3 py-2 font-medium">Side</th>
                  <th className="px-3 py-2 text-right font-medium">Lots</th>
                  <th className="px-3 py-2 text-right font-medium">Open</th>
                  <th className="px-3 py-2 text-right font-medium">Now</th>
                  <th className="px-3 py-2 text-right font-medium">SL / TP</th>
                  <th className="px-3 py-2 text-right font-medium">Floating</th>
                </tr>
              </thead>
              <tbody>
                {(data?.open_positions || []).map((p) => (
                  <tr key={p.position_id} className="border-t">
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[10px] text-muted-foreground">{fmtDateTime(p.open_time)}</td>
                    <td className="px-3 py-2 font-medium">{p.symbol}</td>
                    <td className="px-3 py-2"><DirBadge type={p.type} /></td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.lots}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.open_price}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.current_price}</td>
                    <td className="px-3 py-2 text-right font-mono text-[10px] tabular-nums text-muted-foreground">{p.sl} / {p.tp}</td>
                    <td className={cn("px-3 py-2 text-right font-medium tabular-nums", pnlClass(p.profit))}>{fmtSignedMoney(p.profit)}</td>
                  </tr>
                ))}
                {!data?.open_positions?.length && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No open positions.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      case "accounts_list":
      case "account_comparison":
      case "journal":
      case "version_history":
        return <SharedListsDetail type={type} item={item} shareToken={shareToken} accountId={accountId} />;
      default:
        // all 12 stat widgets: full stat grid + equity context
        return (
          <div className="space-y-4">
            <SummaryGrid data={data} />
            <EquityCurveChart data={data} height={220} currency={data?.account?.currency} />
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="drilldown-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {meta?.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {account ? `${account.name} · ${rangeLabel(range)}` : rangeLabel(range)}
          </DialogDescription>
        </DialogHeader>
        {renderDetail()}
      </DialogContent>
    </Dialog>
  );
}
